import React, { useState, useCallback, useEffect, useMemo } from "react";
import { GithubPicker, SketchPicker } from "react-color";
import { Popover } from "@blueprintjs/core";
import classNames from "classnames";
import Button, { ButtonStyle } from "components/Button";
import DefaultColorSelectButton from "./components/DefaultColorSelectButton";
import { DIALOG_ACTION_TITLE } from "shared/labels";
import { rgbaToHex } from "utils/data";
import PaletteIcon from "icons/palette.svg";
import * as styles from "./styles.module.scss";
const GITHUB_COLORS = [
    "#E6007D",
    "#00A0E6",
    "#00AA46",
    "#462882",
    "#FF7F00",
    "#008796",
    "#A65628",
    "#F06EA5",
    "#C00000",
    "#FF0000",
    "#FFC000",
    "#FFFF00",
    "#92D050",
    "#00B050",
    "#00B0F0",
    "#0070C0",
    "#002060",
    "#7030A0",
];
const ColorPicker = ({ value, className, targetTagName, targetClassName, onChange, defaultButtonClassName, disabled, selectButton: SelectButton, }) => {
    const [color, setColor] = useState(value);
    const [showDefaultPalette, setDefaultPalette] = useState(false);
    const [showExtendedPalette, setExtendedPalette] = useState(false);
    const closeAllPopovers = useCallback(() => {
        setDefaultPalette(false);
        setExtendedPalette(false);
    }, []);
    const handleDefaultPickerChange = useCallback(({ hex }) => {
        setColor(hex);
        onChange(hex);
        closeAllPopovers();
    }, [onChange, closeAllPopovers]);
    const handleExtendedPickerChange = useCallback(({ hex }) => setColor(hex), [setColor]);
    const onExtendedPickerSubmit = useCallback(() => {
        if (color) {
            const hex = rgbaToHex(color);
            onChange(hex);
        }
        closeAllPopovers();
    }, [closeAllPopovers, color, onChange]);
    const onExtendedPickerCancel = useCallback(() => {
        setColor(value);
        closeAllPopovers();
    }, [closeAllPopovers, value]);
    const toggleDefaultPalette = useCallback(() => {
        setDefaultPalette(!showDefaultPalette);
        setExtendedPalette(false);
    }, [showDefaultPalette]);
    const toggleExtendedPalette = useCallback(() => setExtendedPalette(!showExtendedPalette), [showExtendedPalette]);
    useEffect(() => {
        setColor(value);
    }, [value]);
    const buttonProps = useMemo(() => ({
        value,
        disabled,
        className: defaultButtonClassName,
        isActive: showDefaultPalette || showExtendedPalette,
        onClick: toggleDefaultPalette,
    }), [defaultButtonClassName, disabled, showDefaultPalette, showExtendedPalette, toggleDefaultPalette, value]);
    return (React.createElement(Popover, { minimal: true, captureDismiss: true, canEscapeKeyClose: true, placement: "bottom-end", interactionKind: "click", onClose: closeAllPopovers, isOpen: showDefaultPalette, popoverClassName: styles.popoverContainer, disabled: disabled, modifiers: {
            preventOverflow: {
                enabled: true,
                fn: (data) => {
                    const popperInstance = data?.instance?.state?.attributes?.popper;
                    const popperInstanceIsHidden = popperInstance && popperInstance["data-popper-reference-hidden"];
                    if (popperInstanceIsHidden) {
                        closeAllPopovers();
                    }
                },
            },
        }, targetTagName: targetTagName, className: targetClassName, content: React.createElement(Popover, { minimal: true, captureDismiss: true, canEscapeKeyClose: true, placement: "right-start", interactionKind: "click", isOpen: showExtendedPalette, content: React.createElement("div", { className: styles.sketchContainer },
                React.createElement(SketchPicker, { disableAlpha: true, color: color ?? undefined, onChangeComplete: handleExtendedPickerChange, className: styles.sketchPicker }),
                React.createElement("div", { className: styles.container },
                    React.createElement(Button, { style: ButtonStyle.Primary, label: DIALOG_ACTION_TITLE.OK, onClick: onExtendedPickerSubmit }),
                    React.createElement(Button, { style: ButtonStyle.Secondary, label: DIALOG_ACTION_TITLE.CANCEL, onClick: onExtendedPickerCancel }))) },
            React.createElement("div", { className: styles.root },
                React.createElement(GithubPicker, { triangle: "hide", colors: GITHUB_COLORS, onChange: handleDefaultPickerChange, className: classNames(styles.githubPicker, className) }),
                React.createElement("div", { className: styles.iconContainer, onClick: toggleExtendedPalette },
                    React.createElement(PaletteIcon, null),
                    "More Colours"))) }, SelectButton ? React.createElement(SelectButton, { ...buttonProps }) : React.createElement(DefaultColorSelectButton, { ...buttonProps })));
};
export default ColorPicker;
